// src/pages/admin/AdminRegisterUser.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUnlockAlt, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Form, Card, Button, InputGroup, Alert } from '@themesberg/react-bootstrap';
import API from "../../utils/api";
import { ToastAlert } from "../ToastAlert";

export default function ClientRegisterUser() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.email.trim()) newErrors.email = t("adminRegisterUserTranslations.errors.emailRequired");
    if (!formData.password) newErrors.password = t("adminRegisterUserTranslations.errors.passwordRequired");
    else if (formData.password.length < 6) newErrors.password = t("adminRegisterUserTranslations.errors.passwordMinLength");
    if (!formData.confirmPassword) newErrors.confirmPassword = t("adminRegisterUserTranslations.errors.confirmPasswordRequired");
    else if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = t("adminRegisterUserTranslations.errors.passwordsDontMatch");
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await API.post("/auth/sub-user-register", formData, { withCredentials: true });
      showAlert(t("adminRegisterUserTranslations.alerts.userRegistered"));
      setFormData({ email: "", password: "", confirmPassword: "" });
    } catch (error) {
      setApiError(error.response?.data?.message || t("adminRegisterUserTranslations.errors.apiError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastAlert
        show={alert.show}
        message={alert.message}
        variant={alert.variant}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <Row className="justify-content-center align-items-center vh-100">
        <Col xs={12} md={10} lg={8} xl={6}>
          <Card className="shadow p-4">
            <Card.Body>
              <h3 className="mb-4 text-center">
                <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                {t("adminRegisterUserTranslations.heading")}
              </h3>

              {apiError && <Alert variant="danger">{apiError}</Alert>}

              <Form onSubmit={handleSubmit}>
                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label>{t("adminRegisterUserTranslations.labels.email")}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FontAwesomeIcon icon={faEnvelope} /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder={t("adminRegisterUserTranslations.placeholders.email")}
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                {/* Password */}
                <Form.Group className="mb-3">
                  <Form.Label>{t("adminRegisterUserTranslations.labels.password")}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FontAwesomeIcon icon={faUnlockAlt} /></InputGroup.Text>
                    <Form.Control
                      type="password"
                      name="password"
                      placeholder={t("adminRegisterUserTranslations.placeholders.password")}
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                {/* Confirm Password */}
                <Form.Group className="mb-3">
                  <Form.Label>{t("adminRegisterUserTranslations.labels.confirmPassword")}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FontAwesomeIcon icon={faUnlockAlt} /></InputGroup.Text>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      placeholder={t("adminRegisterUserTranslations.placeholders.confirmPassword")}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      isInvalid={!!errors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Button type="submit" variant="primary" className="w-100" disabled={loading}>
                  {loading ? t("adminRegisterUserTranslations.buttons.creating") : t("adminRegisterUserTranslations.buttons.createUser")}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
